<template>
  <div class="tool-page">
    <div class="tool-header">
      <h1>{{ $t('exchange.title') }}</h1>
      <p>{{ $t('exchange.subtitle') }}</p>
    </div>

    <div class="tool-card">
      <div class="exchange-form">
        <div class="currency-input">
          <div class="input-wrapper">
            <input 
              type="number" 
              v-model.number="amount" 
              :placeholder="$t('exchange.inputPlaceholder')"
              @input="convertCurrency"
            >
          </div>
          <select v-model="fromCurrency" @change="convertCurrency" class="currency-select">
            <option v-for="currency in currencies" :key="currency.code" :value="currency.code">
              {{ currency.flag }} {{ currency.code }} - {{ $t('exchange.currencies.' + currency.code) }}
            </option>
          </select>
        </div>

        <div class="swap-btn" @click="swapCurrencies">
          <span>⇅</span>
        </div>

        <div class="currency-input">
          <div class="input-wrapper">
            <input 
              type="number" 
              v-model.number="result" 
              readonly
              :placeholder="$t('exchange.resultPlaceholder')"
            >
          </div>
          <select v-model="toCurrency" @change="convertCurrency" class="currency-select">
            <option v-for="currency in currencies" :key="currency.code" :value="currency.code">
              {{ currency.flag }} {{ currency.code }} - {{ $t('exchange.currencies.' + currency.code) }}
            </option>
          </select>
        </div>
      </div>

      <div class="rate-info" v-if="currentRate">
        <div class="rate-display">
          <span class="rate-label">{{ $t('exchange.currentRate') }}：</span>
          <span class="rate-value">1 {{ fromCurrency }} = {{ currentRate.toFixed(4) }} {{ toCurrency }}</span>
        </div>
        <div class="rate-reverse">
          <span class="rate-label">{{ $t('exchange.reverseRate') }}：</span>
          <span class="rate-value">1 {{ toCurrency }} = {{ (1/currentRate).toFixed(4) }} {{ fromCurrency }}</span>
        </div>
      </div>

      <div class="quick-convert">
        <h3>{{ $t('exchange.quickConvert') }}</h3>
        <div class="quick-grid">
          <div class="quick-item" v-for="quickAmount in [100, 500, 1000, 5000, 10000]" :key="quickAmount">
            <span class="quick-from">{{ quickAmount }} {{ fromCurrency }}</span>
            <span class="quick-arrow">→</span>
            <span class="quick-to">{{ formatNumber(quickAmount * currentRate) }} {{ toCurrency }}</span>
          </div>
        </div>
      </div>

      <div class="rates-table">
        <h3>{{ $t('exchange.ratesTable') }}</h3>
        <div class="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>{{ $t('exchange.currency') }}</th>
                <th>{{ $t('exchange.code') }}</th>
                <th>{{ $t('exchange.toCny') }}</th>
                <th>{{ $t('exchange.fromCny') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="currency in currencies.filter(c => c.code !== 'CNY')" :key="currency.code">
                <td>{{ currency.flag }} {{ $t('exchange.currencies.' + currency.code) }}</td>
                <td>{{ currency.code }}</td>
                <td>{{ (1 / currency.rate).toFixed(4) }}</td>
                <td>{{ currency.rate.toFixed(4) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="refresh-section">
        <button class="refresh-btn" @click="fetchRates" :disabled="loading">
          <span v-if="loading">🔄 {{ $t('exchange.refreshing') }}</span>
          <span v-else>🔄 {{ $t('exchange.refreshRates') }}</span>
        </button>
        <span class="data-source" :class="{ online: isOnlineData }">
          {{ isOnlineData ? '✅ ' + $t('exchange.liveData') : '📦 ' + $t('exchange.localData') }}
        </span>
      </div>

      <div class="disclaimer">
        <p>⚠️ {{ $t('exchange.disclaimer') }}</p>
        <p>{{ $t('exchange.updateTime') }}：{{ updateTime }}</p>
        <p v-if="isOnlineData">{{ $t('exchange.dataSource') }}：ExchangeRate-API</p>
      </div>
    </div>

    <!-- 工具说明 -->
    <div class="tool-description">
      <h2>{{ $t('exchange.whatIsExchangeRate') }}</h2>
      <p>{{ $t('exchange.whatIsExchangeRateDesc') }}</p>

      <h2>{{ $t('exchange.exchangeRateTypes') }}</h2>
      <ul>
        <li v-for="(item, index) in $t('exchange.exchangeRateTypesItems')" :key="'type'+index">{{ item }}</li>
      </ul>

      <h2>{{ $t('exchange.factorsAffectingRates') }}</h2>
      <ul>
        <li v-for="(item, index) in $t('exchange.factorsAffectingRatesItems')" :key="'factor'+index">{{ item }}</li>
      </ul>

      <h2>{{ $t('exchange.exchangeTips') }}</h2>
      <ul>
        <li v-for="(item, index) in $t('exchange.exchangeTipsItems')" :key="'tip'+index">{{ item }}</li>
      </ul>

      <h2>{{ $t('exchange.currencySymbols') }}</h2>
      <p>{{ $t('exchange.currencySymbolsDesc') }}</p>

      <h2>{{ $t('exchange.rmbInternationalization') }}</h2>
      <p>{{ $t('exchange.rmbInternationalizationDesc') }}</p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ExchangeCalculator',
  data() {
    return {
      amount: 1000,
      fromCurrency: 'CNY',
      toCurrency: 'USD',
      result: 0,
      loading: false,
      isOnlineData: false,
      // 汇率以人民币为基准 (1 外币 = X 人民币)，这是默认的本地数据
      defaultRates: {
        CNY: 1,
        USD: 7.24,
        EUR: 7.85,
        GBP: 9.15,
        JPY: 0.0483,
        KRW: 0.00527,
        HKD: 0.927,
        TWD: 0.223,
        SGD: 5.38,
        AUD: 4.72,
        CAD: 5.28,
        CHF: 8.12,
        THB: 0.21,
        MYR: 1.62,
        RUB: 0.079
      },
      currencies: [
        { code: 'CNY', flag: '🇨🇳', rate: 1 },
        { code: 'USD', flag: '🇺🇸', rate: 7.24 },
        { code: 'EUR', flag: '🇪🇺', rate: 7.85 },
        { code: 'GBP', flag: '🇬🇧', rate: 9.15 },
        { code: 'JPY', flag: '🇯🇵', rate: 0.0483 },
        { code: 'KRW', flag: '🇰🇷', rate: 0.00527 },
        { code: 'HKD', flag: '🇭🇰', rate: 0.927 },
        { code: 'TWD', flag: '🇹🇼', rate: 0.223 },
        { code: 'SGD', flag: '🇸🇬', rate: 5.38 },
        { code: 'AUD', flag: '🇦🇺', rate: 4.72 },
        { code: 'CAD', flag: '🇨🇦', rate: 5.28 },
        { code: 'CHF', flag: '🇨🇭', rate: 8.12 },
        { code: 'THB', flag: '🇹🇭', rate: 0.21 },
        { code: 'MYR', flag: '🇲🇾', rate: 1.62 },
        { code: 'RUB', flag: '🇷🇺', rate: 0.079 }
      ],
      updateTime: ''
    }
  },
  computed: {
    currentRate() {
      const fromRate = this.currencies.find(c => c.code === this.fromCurrency)?.rate || 1
      const toRate = this.currencies.find(c => c.code === this.toCurrency)?.rate || 1
      // 转换: amount * fromRate / toRate
      return fromRate / toRate
    }
  },
  mounted() {
    this.updateTime = new Date().toLocaleString('zh-CN')
    this.fetchRates() // 自动获取实时汇率
  },
  methods: {
    async fetchRates() {
      this.loading = true
      try {
        // 使用免费的 ExchangeRate-API
        const response = await fetch('https://api.exchangerate-api.com/v4/latest/CNY')
        if (!response.ok) throw new Error('API请求失败')
        
        const data = await response.json()
        
        // 更新汇率数据 (API返回的是 1 CNY = X 外币，需要转换)
        this.currencies.forEach(currency => {
          if (currency.code === 'CNY') {
            currency.rate = 1
          } else if (data.rates[currency.code]) {
            // API返回: 1 CNY = X 外币
            // 我们需要: 1 外币 = X CNY
            currency.rate = 1 / data.rates[currency.code]
          }
        })
        
        this.isOnlineData = true
        this.updateTime = new Date().toLocaleString('zh-CN')
        this.convertCurrency()
      } catch (error) {
        console.log('获取实时汇率失败，使用本地数据:', error.message)
        // 失败时使用默认汇率
        this.currencies.forEach(currency => {
          currency.rate = this.defaultRates[currency.code] || 1
        })
        this.isOnlineData = false
        this.updateTime = new Date().toLocaleString('zh-CN') + ' (本地数据)'
        this.convertCurrency()
      } finally {
        this.loading = false
      }
    },

    convertCurrency() {
      if (!this.amount) {
        this.result = 0
        return
      }
      this.result = parseFloat((this.amount * this.currentRate).toFixed(2))
    },

    swapCurrencies() {
      const temp = this.fromCurrency
      this.fromCurrency = this.toCurrency
      this.toCurrency = temp
      this.convertCurrency()
    },

    formatNumber(value) {
      if (!value) return '0.00'
      return value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    }
  }
}
</script>

<style scoped>
.tool-page {
  max-width: 800px;
  margin: 0 auto;
}

.tool-header {
  text-align: center;
  margin-bottom: 32px;
}

.tool-header h1 {
  font-size: 32px;
  margin-bottom: 12px;
  color: var(--text-primary);
}

.tool-header p {
  color: var(--text-secondary);
  font-size: 16px;
}

.tool-card {
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 24px;
  box-shadow: var(--shadow-md);
}

.exchange-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
}

.currency-input {
  display: flex;
  gap: 12px;
}

@media (max-width: 600px) {
  .currency-input {
    flex-direction: column;
  }
}

.input-wrapper {
  flex: 1;
}

.input-wrapper input {
  width: 100%;
  background: var(--bg-input);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 16px;
  color: var(--text-primary);
  font-size: 24px;
  font-weight: 600;
}

.input-wrapper input:focus {
  outline: none;
  border-color: var(--accent-color);
}

.input-wrapper input[readonly] {
  background: var(--bg-tertiary);
}

.currency-select {
  min-width: 200px;
  background: var(--bg-input);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 12px 16px;
  color: var(--text-primary);
  font-size: 14px;
  cursor: pointer;
}

.currency-select:focus {
  outline: none;
  border-color: var(--accent-color);
}

.swap-btn {
  align-self: center;
  width: 48px;
  height: 48px;
  background: var(--accent-light);
  border: 1px solid var(--accent-color);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 20px;
  color: var(--accent-color);
  transition: all 0.2s;
}

.swap-btn:hover {
  background: var(--accent-color);
  color: white;
}

.rate-info {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  padding: 16px;
  background: var(--bg-tertiary);
  border-radius: 12px;
  margin-bottom: 24px;
}

.rate-display, .rate-reverse {
  display: flex;
  align-items: center;
  gap: 8px;
}

.rate-label {
  color: var(--text-secondary);
  font-size: 14px;
}

.rate-value {
  color: var(--text-primary);
  font-weight: 600;
  font-size: 14px;
}

.quick-convert {
  margin-bottom: 24px;
}

.quick-convert h3,
.rates-table h3 {
  font-size: 16px;
  margin-bottom: 16px;
  color: var(--text-primary);
}

.quick-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

.quick-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: var(--bg-tertiary);
  border-radius: 8px;
  font-size: 14px;
}

.quick-from {
  color: var(--text-primary);
  font-weight: 500;
}

.quick-arrow {
  color: var(--text-muted);
}

.quick-to {
  color: var(--accent-color);
  font-weight: 600;
}

.rates-table {
  margin-bottom: 24px;
}

.table-wrapper {
  overflow-x: auto;
}

.rates-table table {
  width: 100%;
  border-collapse: collapse;
}

.rates-table th,
.rates-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid var(--border-color);
}

.rates-table th {
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  font-weight: 500;
  font-size: 14px;
}

.rates-table td {
  color: var(--text-primary);
  font-size: 14px;
}

.rates-table td:nth-child(3),
.rates-table td:nth-child(4) {
  font-family: 'Monaco', 'Menlo', monospace;
}

.disclaimer {
  padding: 16px;
  background: var(--bg-tertiary);
  border-radius: 8px;
  font-size: 13px;
  color: var(--text-secondary);
}

.disclaimer p {
  margin: 4px 0;
}

.refresh-section {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
}

.refresh-btn {
  padding: 10px 20px;
  background: var(--accent-color);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.refresh-btn:hover:not(:disabled) {
  background: var(--accent-hover);
  transform: translateY(-1px);
}

.refresh-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.data-source {
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 13px;
  background: var(--bg-tertiary);
  color: var(--text-secondary);
}

.data-source.online {
  background: rgba(34, 197, 94, 0.15);
  color: #22c55e;
}

.tool-description {
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 32px;
  margin-top: 32px;
  box-shadow: var(--shadow-md);
}

.tool-description h2 {
  font-size: 20px;
  color: var(--text-primary);
  margin: 28px 0 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--border-color);
}

.tool-description h2:first-child {
  margin-top: 0;
}

.tool-description p {
  font-size: 15px;
  color: var(--text-secondary);
  line-height: 1.8;
  margin-bottom: 12px;
}

.tool-description ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.tool-description li {
  font-size: 15px;
  color: var(--text-secondary);
  line-height: 1.8;
  padding-left: 20px;
  position: relative;
  margin-bottom: 8px;
}

.tool-description li::before {
  content: '•';
  color: var(--accent-color);
  position: absolute;
  left: 0;
}
</style>
